import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../context/PageTitleContext';
import { useSidebar } from '../context/SidebarContext';

const TopNavbar = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { pageTitle, pageSubtitle } = usePageTitle();
  const { isCollapsed, isMobile, setIsMobileOpen } = useSidebar();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleProfileClick = () => {
    setShowDropdown(!showDropdown);
  };

  const handleViewProfile = () => {
    navigate('/profile');
    setShowDropdown(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    setShowDropdown(false);
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user?.name) return 'U';
    const names = user.name.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return user.name[0].toUpperCase();
  };

  return (
    <div className={`fixed top-0 ${isMobile ? 'left-0' : (isCollapsed ? 'left-16' : 'left-64')} right-0 h-16 bg-white border-b border-gray-200 z-50`}>
      <div className="h-full flex items-center justify-between px-4 md:px-6 gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {/* The sidebar is off-screen on a phone, so it needs a way back */}
          {isMobile && (
            <button
              onClick={() => setIsMobileOpen(true)}
              aria-label="Open menu"
              className="p-2 -ml-2 rounded-md text-gray-600 hover:bg-gray-100 flex-shrink-0"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M3 6h18M3 12h18M3 18h18" />
              </svg>
            </button>
          )}
          <div className="flex flex-col min-w-0">
            <h1 className="text-base md:text-xl font-semibold text-gray-800 leading-tight truncate">{pageTitle}</h1>
            {pageSubtitle && <p className="text-xs text-gray-500 truncate hidden sm:block">{pageSubtitle}</p>}
          </div>
        </div>
        
        <div className="relative" ref={dropdownRef}>
          <button 
            className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors" 
            onClick={handleProfileClick}
            aria-label="User menu"
          >
            <div className="w-9 h-9 rounded-full bg-primary-600 flex items-center justify-center text-white font-semibold text-sm">
              {getUserInitials()}
            </div>
            <div className="hidden md:flex flex-col items-start">
              <span className="text-sm font-medium text-gray-700">{user?.name || 'User'}</span>
              {/* role_name is the role they actually hold. users.role is a legacy
                  string that is 'user' for everyone created through the Users
                  screen, so it showed "user" under a BioMedical Admin. */}
              <span className="text-xs text-gray-500">{user?.role_name || user?.role || 'User'}</span>
            </div>
            <svg 
              className={`w-5 h-5 text-gray-400 transition-transform ${showDropdown ? 'rotate-180' : ''}`}
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
            >
              <path 
                d="M5 7.5L10 12.5L15 7.5" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {showDropdown && (
            <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-100">
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="font-medium text-sm text-gray-900">{user?.name}</div>
                <div className="text-xs text-gray-500 mt-1">{user?.email}</div>
              </div>
              <button 
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors" 
                onClick={handleViewProfile}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                View Profile
              </button>
              <button 
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-danger-600 hover:bg-danger-50 transition-colors" 
                onClick={handleLogout}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 17L21 12L16 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopNavbar;
